/**
 * useUnreadReplies Hook
 *
 * Tracks the number of unread email replies for the current user.
 * Polls every 30 seconds to check for new emails.
 *
 * Usage:
 *   const { unreadCount, loading, refresh } = useUnreadReplies();
 */

import { useState, useEffect } from 'react';
import emailReplyService from '../services/EmailReplyService';
import { useAuth } from '../contexts/AuthContext';
import logger from '../utils/logger';

export const useUnreadReplies = () => {
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadUnreadCount = async () => {
    if (!user?.userId) {
      setUnreadCount(0);
      setLoading(false);
      return;
    }

    try {
      const replies = await emailReplyService.listRepliesByUser(user.userId, {
        status: 'UNREAD'
      });

      setUnreadCount(replies.length);
      setError(null);

      logger.debug('Loaded unread count', { count: replies.length });
    } catch (err) {
      logger.error('Error loading unread count', { error: err.message });
      setError(err.message);
      setUnreadCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    loadUnreadCount();
  }, [user?.userId]);

  // Poll every 30 seconds for new emails
  useEffect(() => {
    if (!user) return;

    const interval = setInterval(() => {
      loadUnreadCount();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [user?.userId]);

  return {
    unreadCount,
    loading,
    error,
    refresh: loadUnreadCount
  };
};
